const TR_TAB_HOST = "tr-tab.comfortweather.com";
const WWW_HOST = "www.comfortweather.com";
const TRAN_LS_TAB_CHANNEL = "tran_ls_tab";

const LANGUAGE_ALIAS_MAP: Record<string, string> = {
  in: "id",
  iw: "he",
};

const EXACT_LANGUAGE_REGION_MAP: Record<string, string> = {
  // 图2明确特殊处理
  es_us: "es-mx",
  es_es: "es",
  es_mx: "es-mx",
  pt_br: "pt",
  pt_pt: "pt-pt",
  sr_rs: "sr-rs",
  sr_me: "sr-rs",
  my_mm: "en",
  my_qaag: "en",
  zh_tw: "zh-hant",
  zh_hk: "zh-hant",
  zh_mo: "zh-hant",
  zh_cn: "zh-hans",
  zh_sg: "zh-hans",
  zh_hans: "zh-hans",
  zh_hant: "zh-hant",

  // 表格中已知常见组合
  en_gb: "en-gb",
  bn_bd: "bn-bd",
  bn_in: "bn-bd",
  ur_in: "ur",
  ur_pk: "ur",
  de_de: "de-de",
  fr_fr: "fr-fr",
  hr_hr: "hr-hr",
  it_it: "it-it",
  hu_hu: "hu-hu",
  pl_pl: "pl-pl",
  ro_ro: "ro-ro",
  sk_sk: "sk",
  vi_vn: "vi",
  tr_tr: "tr-tr",
  el_gr: "el-gr",
  ru_ru: "ru-ru",
  fa_ir: "fa-ir",
  ja_jp: "ja-jp",
  ko_kr: "ko",
  cs_cz: "cs",
  hi_in: "hi",
  th_th: "th",
  ar_eg: "ar",
  he_il: "he",
  id_id: "id",
};

const LANGUAGE_ONLY_MAP: Record<string, string> = {
  cs: "cs",
  de: "de-de",
  en: "en",
  es: "es",
  fr: "fr-fr",
  hr: "hr-hr",
  id: "id",
  it: "it-it",
  hu: "hu-hu",
  pl: "pl-pl",
  pt: "pt",
  ro: "ro-ro",
  sk: "sk",
  vi: "vi",
  tr: "tr-tr",
  el: "el-gr",
  bg: "bg",
  ru: "ru-ru",
  sr: "sr-rs",
  uk: "uk",
  he: "he",
  ur: "ur",
  ar: "ar",
  fa: "fa-ir",
  hi: "hi",
  bn: "bn-bd",
  th: "th",
  ko: "ko",
  ja: "ja-jp",
  zh: "zh-hans",
  my: "en",
};

const DIRECT_LANGUAGE_TAG_MAP: Record<string, string> = {
  "zh-hans": "zh-hans",
  "zh-hant": "zh-hant",
  "es-mx": "es-mx",
  "en-gb": "en-gb",
  "de-de": "de-de",
  "fr-fr": "fr-fr",
  "hr-hr": "hr-hr",
  "it-it": "it-it",
  "hu-hu": "hu-hu",
  "pl-pl": "pl-pl",
  "pt-pt": "pt-pt",
  "ro-ro": "ro-ro",
  "tr-tr": "tr-tr",
  "el-gr": "el-gr",
  "ru-ru": "ru-ru",
  "fa-ir": "fa-ir",
  "bn-bd": "bn-bd",
  "ja-jp": "ja-jp",
  "es-es": "es",
  "en-us": "en",
  "en-au": "en",
  "en-in": "en",
  "pt-br": "pt",
};

export interface TrTabHostContext {
  hostName?: string | null;
  subdomain?: string | null;
  domain?: string | null;
  channel?: string | null;
}

export interface TrTabLanguageInput {
  lan?: string | null;
  con?: string | null;
}

function normalizeCountryCode(region: string) {
  return /^[a-z]{2}$/.test(region) ? region.toUpperCase() : "";
}

function normalizeLanValue(rawLan: string | null | undefined) {
  return String(rawLan || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");
}

function normalizeRegionValue(rawRegion: string | null | undefined) {
  return String(rawRegion || "")
    .trim()
    .toLowerCase();
}

function normalizeChannelValue(rawChannel: string | null | undefined) {
  return String(rawChannel || "")
    .trim()
    .toLowerCase();
}

function splitLanAndRegion(normalizedLan: string, normalizedRegion: string) {
  let lang = normalizedLan;
  let region = normalizedRegion;

  if (!region && normalizedLan.includes("-")) {
    const parts = normalizedLan.split("-").filter(Boolean);
    const maybeTail = parts[parts.length - 1] || "";

    if (/^[a-z]{2,3}$/.test(maybeTail)) {
      region = maybeTail;
      lang = parts[0];
    } else if (["hans", "hant"].includes(maybeTail) && parts[0] === "zh") {
      region = maybeTail;
      lang = "zh";
    } else {
      lang = parts[0];
    }
  }

  if (lang.includes("-")) {
    lang = lang.split("-")[0];
  }

  lang = LANGUAGE_ALIAS_MAP[lang] ?? lang;
  return { lang, region };
}

/**
 * 说明：
 * - 线上 tr-tab 域名：tr-tab.comfortweather.com
 * - 测试环境：test1.comfortweather.com + subdomain=tr-tab
 * - 新增 www 传音渠道：www.comfortweather.com + channel=tran_ls_tab
 * 三种场景都走同一套 tr-tab 语言规则。
 */
export function isTrTabLanguageHost(context: TrTabHostContext) {
  const hostName = String(context.hostName || "").toLowerCase();
  const subdomain = String(context.subdomain || "").toLowerCase();
  const domain = String(context.domain || "").toLowerCase();
  const channel = normalizeChannelValue(context.channel);
  const isTrTabContext = hostName === TR_TAB_HOST || domain === TR_TAB_HOST || subdomain === "tr-tab";
  const isTranLsTabOnWww =
    (hostName === WWW_HOST || domain === WWW_HOST || subdomain === "www") && channel === TRAN_LS_TAB_CHANNEL;

  return isTrTabContext || isTranLsTabOnWww;
}

/**
 * 统一解析 tr-tab 的 Boyuan 语言编码：
 * 1) 归一化（大小写、历史别名）
 * 2) 优先精确匹配 lang_region
 * 3) 退化到语言级映射
 * 4) 最终英文兜底
 */
export function resolveTrTabLanguageCode(input: TrTabLanguageInput) {
  const normalizedLan = normalizeLanValue(input.lan);
  const normalizedRegion = normalizeRegionValue(input.con);

  if (!normalizedLan) {
    return "en";
  }

  if (!normalizedRegion && DIRECT_LANGUAGE_TAG_MAP[normalizedLan]) {
    return DIRECT_LANGUAGE_TAG_MAP[normalizedLan];
  }

  const { lang, region } = splitLanAndRegion(normalizedLan, normalizedRegion);
  const exactKey = region ? `${lang}_${region}` : "";

  if (exactKey && EXACT_LANGUAGE_REGION_MAP[exactKey]) {
    return EXACT_LANGUAGE_REGION_MAP[exactKey];
  }

  if (LANGUAGE_ONLY_MAP[lang]) {
    return LANGUAGE_ONLY_MAP[lang];
  }

  return "en";
}

/**
 * 统一提取国家维度：
 * 1) 优先使用显式 `con`
 * 2) 其次从 `lan=en-gb / pt-br / zh-tw` 这类 tag 中拆国家
 * 3) 仅返回可用于统计筛选的两位国家码
 */
export function resolveCountryCodeFromLanguageInput(input: TrTabLanguageInput) {
  const normalizedLan = normalizeLanValue(input.lan);
  const normalizedRegion = normalizeRegionValue(input.con);
  const { region } = splitLanAndRegion(normalizedLan, normalizedRegion);
  return normalizeCountryCode(region);
}
